import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingDown, TrendingUp, Calculator, Lightbulb, ArrowRight, CheckCircle2 } from 'lucide-react';

// Tarifs par défaut (€) - constante statique en dehors du composant
const PRICES = {
  simple: 0.01,
  riche: 0.02,
  rich_media: 0.04,
  conversation: 0.05,
  fallback_sms: 0.03
};

const CostSimulator = () => {
  const { t } = useTranslation();
  // État du simulateur
  const [volume, setVolume] = useState(1000);
  const [messageType, setMessageType] = useState('riche');
  const [responseRate, setResponseRate] = useState(30);
  const [messagesPerConversation, setMessagesPerConversation] = useState(5);
  const [fallbackRate, setFallbackRate] = useState(20);
  
  // Calculs automatiques
  const [results, setResults] = useState({
    nonConversational: { total: 0, breakdown: {} },
    conversational: { total: 0, breakdown: {} },
    savings: 0,
    savingsPercentage: 0,
    recommendation: ''
  });

  const calculateCosts = useCallback(() => {
    // Mode Non-Conversationnel
    const messagePrice = PRICES[messageType];
    const messagesWithFallback = Math.floor(volume * (fallbackRate / 100));
    const rcsMessages = volume - messagesWithFallback;
    
    const nonConvCost = (rcsMessages * messagePrice) + (messagesWithFallback * PRICES.fallback_sms);
    
    // Mode Conversationnel
    // On estime qu'avec un taux de réponse de X%, on a X% de conversations
    const estimatedConversations = Math.floor(volume * (responseRate / 100) / messagesPerConversation);
    const convCost = (estimatedConversations * PRICES.conversation) + (messagesWithFallback * PRICES.fallback_sms);
    
    // Calcul économies
    const savings = nonConvCost - convCost;
    const savingsPercentage = nonConvCost > 0 ? ((savings / nonConvCost) * 100) : 0;
    
    // Recommandation
    let recommendation = '';
    if (savingsPercentage > 50) {
      recommendation = 'conversationnel_high';
    } else if (savingsPercentage > 20) {
      recommendation = 'conversationnel_medium';
    } else if (savingsPercentage > 0) {
      recommendation = 'conversationnel_low';
    } else {
      recommendation = 'non_conversationnel';
    }
    
    setResults({
      nonConversational: {
        total: nonConvCost,
        breakdown: {
          rcs_messages: rcsMessages,
          rcs_cost: rcsMessages * messagePrice,
          fallback_count: messagesWithFallback,
          fallback_cost: messagesWithFallback * PRICES.fallback_sms
        }
      },
      conversational: {
        total: convCost,
        breakdown: {
          conversations: estimatedConversations,
          conversation_cost: estimatedConversations * PRICES.conversation,
          messages_in_conversations: volume - messagesWithFallback,
          fallback_count: messagesWithFallback,
          fallback_cost: messagesWithFallback * PRICES.fallback_sms
        }
      },
      savings: savings,
      savingsPercentage: savingsPercentage,
      recommendation: recommendation
    });
  }, [volume, messageType, responseRate, messagesPerConversation, fallbackRate]);

  useEffect(() => {
    calculateCosts();
  }, [calculateCosts]);

  const comparisonData = [
    {
      name: 'Non-conversationnel',
      cost: results.nonConversational.total,
      fill: '#ef4444'
    },
    {
      name: 'Conversationnel',
      cost: results.conversational.total,
      fill: '#22c55e'
    }
  ];

  const breakdownData = [
    { name: 'Messages RCS', value: results.nonConversational.breakdown.rcs_cost || 0, color: '#6366f1' },
    { name: 'Fallback SMS', value: results.nonConversational.breakdown.fallback_cost || 0, color: '#f59e0b' }
  ];

  const getRecommendationContent = () => {
    const rec = results.recommendation;
    
    if (rec === 'conversationnel_high') {
      return {
        title: 'Mode Conversationnel Fortement Recommandé',
        description: `Avec ${Math.round(results.savingsPercentage)}% d'économie, le mode conversationnel est le choix optimal pour votre usage.`,
        icon: <CheckCircle2 className="h-6 w-6 text-green-600" />,
        color: 'bg-green-50 border-green-200',
        textColor: 'text-green-900'
      };
    } else if (rec === 'conversationnel_medium') {
      return {
        title: 'Mode Conversationnel Recommandé',
        description: `${Math.round(results.savingsPercentage)}% d'économie avec le mode conversationnel. Un bon choix pour des interactions client.`,
        icon: <CheckCircle2 className="h-6 w-6 text-blue-600" />,
        color: 'bg-blue-50 border-blue-200',
        textColor: 'text-blue-900'
      };
    } else if (rec === 'conversationnel_low') {
      return {
        title: 'Légère Économie en Mode Conversationnel',
        description: `${Math.round(results.savingsPercentage)}% d'économie possible. Évaluez selon votre stratégie de communication.`,
        icon: <Lightbulb className="h-6 w-6 text-yellow-600" />,
        color: 'bg-yellow-50 border-yellow-200',
        textColor: 'text-yellow-900'
      };
    } else {
      return {
        title: 'Mode Non-Conversationnel Recommandé',
        description: 'Pour des notifications one-way sans réponse attendue, le mode non-conversationnel est plus adapté.',
        icon: <Lightbulb className="h-6 w-6 text-indigo-600" />,
        color: 'bg-indigo-50 border-indigo-200',
        textColor: 'text-indigo-900'
      };
    }
  };

  const recommendation = getRecommendationContent();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-8" data-testid="cost-simulator-page">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <div className="rounded-xl bg-indigo-100 p-3">
            <Calculator className="h-8 w-8 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Simulateur de Coûts RCS
            </h1>
            <p className="mt-2 text-slate-600">
              Comparez les coûts entre mode conversationnel et non-conversationnel pour optimiser votre budget
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Panel */}
          <Card className="lg:col-span-1 rounded-2xl border-slate-100">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Configuration</CardTitle>
              <CardDescription>Ajustez les paramètres de simulation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Volume de messages */}
              <div className="space-y-3">
                <Label htmlFor="volume" className="text-sm font-semibold">
                  Volume de messages mensuel
                </Label>
                <Input
                  id="volume"
                  type="number"
                  value={volume}
                  onChange={(e) => setVolume(parseInt(e.target.value) || 0)}
                  className="rounded-xl h-12"
                  data-testid="volume-input"
                />
                <Slider
                  value={[volume]}
                  onValueChange={(val) => setVolume(val[0])}
                  max={50000}
                  step={100}
                  className="mt-2"
                />
                <p className="text-xs text-slate-500">{volume.toLocaleString()} messages/mois</p>
              </div>

              {/* Type de message */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Type de message</Label>
                <Select value={messageType} onValueChange={setMessageType}>
                  <SelectTrigger className="rounded-xl h-12" data-testid="message-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple">Simple (€0.01)</SelectItem>
                    <SelectItem value="riche">Riche (€0.02)</SelectItem>
                    <SelectItem value="rich_media">Rich Media (€0.04)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Taux de réponse */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">
                  Taux de réponse estimé ({responseRate}%)
                </Label>
                <Slider
                  value={[responseRate]}
                  onValueChange={(val) => setResponseRate(val[0])}
                  max={100}
                  step={5}
                />
                <p className="text-xs text-slate-500">
                  Pourcentage d'utilisateurs qui répondent à vos messages
                </p>
              </div>

              {/* Messages par conversation */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">
                  Messages par conversation ({messagesPerConversation})
                </Label>
                <Slider
                  value={[messagesPerConversation]}
                  onValueChange={(val) => setMessagesPerConversation(val[0])}
                  min={1}
                  max={20}
                  step={1}
                />
                <p className="text-xs text-slate-500">
                  Nombre moyen de messages échangés dans une conversation
                </p>
              </div>

              {/* Taux fallback */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold">
                  Taux fallback SMS ({fallbackRate}%)
                </Label>
                <Slider
                  value={[fallbackRate]}
                  onValueChange={(val) => setFallbackRate(val[0])}
                  max={100}
                  step={5}
                />
                <p className="text-xs text-slate-500">
                  Pourcentage de destinataires non-compatibles RCS
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recommandation */}
            <Alert className={`border-2 ${recommendation.color}`}>
              <div className="flex items-start space-x-3">
                {recommendation.icon}
                <div className="flex-1">
                  <h3 className={`font-semibold ${recommendation.textColor}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {recommendation.title}
                  </h3>
                  <AlertDescription className={recommendation.textColor}>
                    {recommendation.description}
                  </AlertDescription>
                </div>
              </div>
            </Alert>

            {/* Économies */}
            {results.savings > 0 && (
              <Card className="rounded-2xl border-green-100 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-700">Économies Potentielles</p>
                      <p className="mt-2 text-4xl font-bold text-green-900" data-testid="savings-amount">
                        €{results.savings.toFixed(2)}
                      </p>
                      <p className="mt-1 text-sm text-green-600">
                        {results.savingsPercentage.toFixed(1)}% d'économie par mois
                      </p>
                    </div>
                    <div className="rounded-full bg-green-100 p-4">
                      <TrendingDown className="h-10 w-10 text-green-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Comparison Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Non-Conversationnel */}
              <Card className="rounded-2xl border-slate-100">
                <CardHeader className="bg-slate-50 rounded-t-2xl">
                  <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Mode Non-Conversationnel
                  </CardTitle>
                  <CardDescription>Facturation par message</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <p className="text-4xl font-bold text-slate-900" data-testid="non-conv-total">
                      €{results.nonConversational.total.toFixed(2)}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">Coût mensuel total</p>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Messages RCS:</span>
                      <span className="font-semibold">{results.nonConversational.breakdown.rcs_messages}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Coût RCS:</span>
                      <span className="font-semibold">€{results.nonConversational.breakdown.rcs_cost?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Fallback SMS:</span>
                      <span className="font-semibold">{results.nonConversational.breakdown.fallback_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Coût Fallback:</span>
                      <span className="font-semibold">€{results.nonConversational.breakdown.fallback_cost?.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Conversationnel */}
              <Card className="rounded-2xl border-green-200 bg-green-50">
                <CardHeader className="bg-green-100 rounded-t-2xl">
                  <CardTitle className="text-lg text-green-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Mode Conversationnel
                  </CardTitle>
                  <CardDescription className="text-green-700">Facturation par conversation (24h)</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <p className="text-4xl font-bold text-green-900" data-testid="conv-total">
                      €{results.conversational.total.toFixed(2)}
                    </p>
                    <p className="text-sm text-green-600 mt-1">Coût mensuel total</p>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between text-green-900">
                      <span>Conversations:</span>
                      <span className="font-semibold">{results.conversational.breakdown.conversations}</span>
                    </div>
                    <div className="flex justify-between text-green-900">
                      <span>Coût conversations:</span>
                      <span className="font-semibold">€{results.conversational.breakdown.conversation_cost?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-900">
                      <span>Messages inclus:</span>
                      <span className="font-semibold">{results.conversational.breakdown.messages_in_conversations}</span>
                    </div>
                    <div className="flex justify-between text-green-900">
                      <span>Fallback SMS:</span>
                      <span className="font-semibold">{results.conversational.breakdown.fallback_count}</span>
                    </div>
                    <div className="flex justify-between text-green-900">
                      <span>Coût Fallback:</span>
                      <span className="font-semibold">€{results.conversational.breakdown.fallback_cost?.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <Tabs defaultValue="comparison" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="comparison">Comparaison</TabsTrigger>
                <TabsTrigger value="breakdown">Répartition</TabsTrigger>
              </TabsList>
              
              <TabsContent value="comparison">
                <Card className="rounded-2xl border-slate-100">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Comparaison des Coûts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="name" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => `€${value.toFixed(2)}`}
                        />
                        <Bar dataKey="cost" radius={[8, 8, 0, 0]}>
                          {comparisonData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="breakdown">
                <Card className="rounded-2xl border-slate-100">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Répartition des Coûts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={breakdownData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: €${entry.value.toFixed(2)}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {breakdownData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Action Card */}
            <Card className="rounded-2xl border-indigo-100 bg-indigo-50">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-indigo-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Prêt à optimiser vos coûts ?
                    </h3>
                    <p className="text-sm text-indigo-700 mb-4">
                      Configurez votre agent RCS avec le mode recommandé et commencez à économiser dès maintenant.
                    </p>
                    <Button
                      className="rounded-full bg-indigo-700"
                      onClick={() => window.location.href = '/settings'}
                      data-testid="configure-agent-button"
                    >
                      Configurer mon agent RCS
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CostSimulator;